export type ConnectivityTone = 'optimal' | 'online' | 'limited' | 'offline';
export type NetworkQuality = 'optimal' | 'online' | 'limited' | 'unknown';
export type BandwidthLevel = 'unknown' | 'low' | 'medium' | 'high';

export type NetworkInformationLike = EventTarget & {
  downlink?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
};

export type ConnectivityStatusLike = {
  backendAvailable: boolean;
  systemOnline: boolean;
};

export const classifyNetworkQuality = (
  connection: NetworkInformationLike | null
): NetworkQuality => {
  if (!connection) return 'unknown';
  const effectiveType = connection.effectiveType;
  const downlink = connection.downlink;
  const rtt = connection.rtt;

  if (
    connection.saveData ||
    effectiveType === 'slow-2g' ||
    effectiveType === '2g' ||
    (typeof downlink === 'number' && downlink < 0.75) ||
    (typeof rtt === 'number' && rtt >= 800)
  ) {
    return 'limited';
  }

  if (
    effectiveType === '3g' ||
    (typeof downlink === 'number' &&
      downlink < 2 &&
      typeof rtt === 'number' &&
      rtt >= 100) ||
    (typeof rtt === 'number' && rtt >= 500)
  ) {
    return 'limited';
  }

  return 'online';
};

export const classifyMeasuredBandwidthLevel = (
  bandwidthMbps: number | null
): BandwidthLevel => {
  if (typeof bandwidthMbps !== 'number') return 'unknown';
  if (bandwidthMbps < 1) return 'low';
  if (bandwidthMbps < 10) return 'medium';
  return 'high';
};

export const classifyMeasuredConnectivityTone = (
  latencyMs: number | null,
  bandwidthLevel: BandwidthLevel
): ConnectivityTone | null => {
  if (latencyMs === null && bandwidthLevel === 'unknown') return null;
  if (
    (typeof latencyMs === 'number' && latencyMs >= 800) ||
    bandwidthLevel === 'low'
  ) {
    return 'limited';
  }
  if (
    typeof latencyMs === 'number' &&
    latencyMs <= 150 &&
    bandwidthLevel === 'high'
  ) {
    return 'optimal';
  }
  return 'online';
};

export const resolveConnectivityTone = (
  connectivity: ConnectivityStatusLike,
  quality: NetworkQuality,
  _browserOnline = connectivity.systemOnline,
  measuredTone: ConnectivityTone | null = null
): ConnectivityTone => {
  if (!connectivity.backendAvailable) return 'offline';
  if (measuredTone) return measuredTone;
  if (quality === 'limited') return 'limited';
  if (quality === 'unknown') return 'online';
  return 'optimal';
};
